"use client"

import { dateLib } from "@agentcall/core/lib"
import { planType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { endOfMonth } from "date-fns"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import * as z from "zod"
import { planApi } from "@/api/client/plan"
import { getErrorMessage } from "@/api/instance"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"

// Form schema
const planCancelFormSchema = z.object({
  cancellationReason: z.enum(planType.PLAN_END_REASON_VALUES, {
    message: "プラン終了理由を選択してください",
  }),
  contractEndDate: z.date({
    message: "賃貸借契約終了日を入力してください",
  }),
  agreement: z.boolean().refine((val) => val === true, {
    message: "承認が必要です",
  }),
})

type PlanCancelFormValues = z.infer<typeof planCancelFormSchema>

export function PlanCancelPage() {
  const { propertyId: propertyIdParam, roomId: roomIdParam, planId: planIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const roomId = roomIdParam ? Number(roomIdParam) : undefined
  const planId = planIdParam ? Number(planIdParam) : undefined
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  console.log("PlanCancelPage params:", { propertyId, roomId, planId })

  // Fetch plan details
  const { data: planData } = planApi.usePlanGet(
    planId,
    {
      withRoom: "true",
      withTenant: "true",
      withRoomProperty: "true",
    },
    {
      query: {
        enabled: !!planId,
      },
    }
  )

  const plan = planData?.plan
  const room = plan?.room
  const tenant = plan?.tenant
  const property = room?.property as { name?: string } | undefined
  const planMaster = plan?.planMaster

  // Plan cancel mutation
  const planCancelMutation = planApi.usePlanCancel({
    mutation: {
      onSuccess: () => {
        toast.success("プランの終了処理が完了しました")
        navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { shouldRefetch: true } })
      },
      onError: (error) => {
        toast.error("プラン終了処理に失敗しました", { description: getErrorMessage(error) })
        console.error(error)
      },
    },
  })

  const form = useForm<PlanCancelFormValues>({
    resolver: zodResolver(planCancelFormSchema),
    defaultValues: {
      cancellationReason: undefined,
      contractEndDate: undefined,
      agreement: false,
    },
  })

  const onSubmit = async (data: PlanCancelFormValues) => {
    if (!planId) {
      toast.error("プランIDが不正です")
      return
    }

    setIsSubmitting(true)
    try {
      // Format the date to YYYY-MM-DD format for the API
      const endDate = dateLib.formatDateISO(endOfMonth(data.contractEndDate))

      await planCancelMutation.mutateAsync({
        id: planId,
        data: {
          endDate,
          endReason: data.cancellationReason,
          contractEndDate: dateLib.formatDateISO(data.contractEndDate),
        },
      })
    } catch (error) {
      // Error handling is done in onError callback
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  // Calculate subscription end date (end of month) based on contract end date
  const contractEndDate = form.watch("contractEndDate")
  const subscriptionEndDate = contractEndDate
    ? dateLib.formatDateJapanese(endOfMonth(contractEndDate))
    : "----年--月--日"

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div>
      <div className="space-y-5">
        <PropertyHeader propertyId={propertyId} />
        <RoomHeader roomId={roomId} />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Contract information */}
            <div className="rounded-md bg-slate-100 p-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-400">物件名</p>
                  <p className="text-sm font-semibold text-primary">{property?.name || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-400">居室番号</p>
                  <p className="text-sm font-semibold text-primary">{room?.name || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-400">契約者氏名</p>
                  <p className="text-sm font-semibold text-primary">{tenant?.name || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-400">利用中のサービス</p>
                  <p className="text-sm font-semibold text-primary">{planMaster?.name || "-"}</p>
                </div>
              </div>
            </div>

            {/* Cancellation reason and contract end date */}
            <Card>
              <CardContent className="space-y-4 p-4">
                {/* Cancellation reason */}
                <FormField
                  control={form.control}
                  name="cancellationReason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        プラン終了理由
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {planType.PLAN_END_REASONS.map((reason) => (
                            <SelectItem key={reason.key} value={reason.key}>
                              {reason.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {/* Contract end date */}
                <FormField
                  control={form.control}
                  name="contractEndDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        賃貸借契約終了日
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="----年--月--日"
                          fromDate={new Date()}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Subscription end date display */}
            <div className="rounded-md bg-slate-100 p-4">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-400">ご請求終了日</p>
                <p
                  className={`text-sm font-semibold ${
                    contractEndDate ? "text-primary" : "text-slate-300"
                  }`}
                >
                  {subscriptionEndDate}
                </p>
              </div>
            </div>

            {/* Agreement checkbox */}
            <Card>
              <CardContent className="p-4">
                <FormField
                  control={form.control}
                  name="agreement"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal text-slate-600">
                          当フォームを持って、サービス並びに損害保険（プラスプランの場合）が終了することを承認します。
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                戻る
              </Button>
              <Button type="submit" disabled={!form.formState.isValid} loading={isSubmitting}>
                完了
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
